import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Text,
  Button,
  Box,
  Heading,
  Checkbox,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  Link,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productApprovalsApi } from '@texas/api/endpoints/productApprovalsApi';
import {
  ContactAddToType,
  SimpleContact,
} from '@texas/api/endpoints/contactsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ContactsModal } from '@texas/components/contacts/ContactsModal';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { SharedDisclosureProps } from '../types';
import { ErrorDetails } from '../alert/ErrorDetails';
import { SpinnerWithLabel } from '../SpinnerWithLabel';
import { productApprovalEvents } from './events';
import {
  ApprovalDraft,
  ApprovalValues,
  SharedApprovalValues,
  applySharedApprovalValue,
  createApprovalDrafts,
  getApprovalIssues,
  getSharedApprovalValues,
} from './bulkApprovalState';
import { BulkApprovalRow, ApprovalContactButton } from './BulkApprovalRow';

interface Props extends SharedDisclosureProps {
  groupIds: number[];
  onSubmit?: () => void;
}

interface ContactSelection {
  field: 'supplierContact' | 'customerContact';
  referenceId: number;
  groupId?: number;
}

interface FormProps {
  groupIds: number[];
  onClose: () => void;
  onSubmit: () => void;
}

export function GeneratePaFromGroupsModal({
  isOpen,
  onClose,
  groupIds,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
      variant="entityDirectory"
    >
      <ModalOverlay />
      <ModalContent maxW="1200px" mx={4} borderRadius="12px" overflow="hidden">
        <ModalHeader
          px={{ base: 4, md: 8 }}
          pt={7}
          pb={6}
          textTransform="none"
          fontSize="28px"
          fontWeight="semibold"
          borderBottomWidth="1px"
          borderColor="entityDirectory.border"
        >
          {t('productApproval.createNew')}
        </ModalHeader>
        <ModalCloseButton top={7} right={6} />
        <ApprovalForm
          groupIds={groupIds}
          onClose={onClose}
          onSubmit={() => {
            productApprovalEvents.productApprovalsCreated.dispatch();
            onSubmit?.();
            onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function ApprovalForm({ groupIds, onClose, onSubmit }: FormProps) {
  const { t } = useTranslation();
  const { data, refetch, loading, error } = useApiResource(
    productApprovalsApi.generate,
  );
  const {
    request: create,
    loading: saving,
    error: saveError,
  } = useApiRequest(productApprovalsApi.create);
  const [drafts, setDrafts] = useState<ApprovalDraft[]>([]);
  const [shared, setShared] = useState<SharedApprovalValues>({
    suppliers: {},
    customers: {},
    sampleReadyDate: null,
  });
  const [overwrite, setOverwrite] = useState(false);
  const selection = useValueDisclosure<ContactSelection>();
  const active = drafts.filter((draft) => !draft.ignored);
  const invalid = active.filter((draft) => getApprovalIssues(draft).length > 0);
  const readyCount = active.length - invalid.length;
  const suppliers = [
    ...new Map(
      (data ?? []).map((sample) => [sample.supplierId, sample]),
    ).values(),
  ];
  const customers = [
    ...new Map(
      (data ?? []).map((sample) => [sample.customerId, sample]),
    ).values(),
  ];
  const initialized = data !== null && drafts.length === data.length;
  const canCreate =
    initialized &&
    !loading &&
    !error &&
    active.length > 0 &&
    invalid.length === 0;

  const setField = <K extends keyof ApprovalValues>(
    groupId: number,
    field: K,
    value: ApprovalValues[K],
  ) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.sample.compositionGroupId === groupId
          ? {
              ...draft,
              [field]: value,
              overrides: { ...draft.overrides, [field]: true },
            }
          : draft,
      ),
    );
  };
  const resetField = (groupId: number, field: keyof ApprovalValues) => {
    setDrafts((current) =>
      current.map((draft) => {
        if (draft.sample.compositionGroupId !== groupId) {
          return draft;
        }
        const value =
          field === 'sampleReadyDate'
            ? shared.sampleReadyDate ??
              draft.sample.expectedReadyDate?.slice(0, 10) ??
              ''
            : field === 'supplierContact'
            ? shared.suppliers[draft.sample.supplierId] ??
              draft.sample.supplierContact
            : shared.customers[draft.sample.customerId] ??
              draft.sample.customerContact;
        return {
          ...draft,
          [field]: value,
          overrides: { ...draft.overrides, [field]: false },
        };
      }),
    );
  };
  const selectContact = (contact: SimpleContact) => {
    const target = selection.value;
    if (!target) {
      return;
    }
    if (target.groupId !== undefined) {
      setField(target.groupId, target.field, contact);
    } else {
      const key =
        target.field === 'supplierContact' ? 'suppliers' : 'customers';
      setShared((current) => ({
        ...current,
        [key]: { ...current[key], [target.referenceId]: contact },
      }));
      setDrafts((current) =>
        applySharedApprovalValue(
          current,
          target.field,
          contact,
          overwrite,
          target.referenceId,
        ),
      );
    }
    selection.onClose();
  };
  const changeSharedDate = (date: string) => {
    setShared((current) => ({ ...current, sampleReadyDate: date || null }));
    if (date) {
      setDrafts((current) =>
        applySharedApprovalValue(current, 'sampleReadyDate', date, overwrite),
      );
    }
  };
  const submit = async () => {
    if (!canCreate || saving) {
      return;
    }
    const response = await create({
      data: active.map((draft) => ({
        branchId: draft.sample.branchId,
        compositionGroupId: draft.sample.compositionGroupId,
        customerContactId: draft.customerContact!.id,
        supplierContactId: draft.supplierContact!.id,
        externalSupplierItemCode: '',
        matrixNodeXId: draft.sample.matrixNodeXId,
        matrixNodeYId: draft.sample.matrixNodeYId,
        supplierId: draft.sample.supplierId,
        supplierNote: '',
        variantId: draft.sample.variantId,
        sampleReadyDate: new Date(`${draft.sampleReadyDate}T00:00:00`),
      })),
    });
    if (!response.hasError) {
      onSubmit();
    }
  };
  const goToApproval = (groupId: number) => {
    const row = document.getElementById(`bulk-pa-${groupId}`);
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row
      ?.querySelector<HTMLElement>('[aria-invalid="true"]')
      ?.focus({ preventScroll: true });
  };

  useEffect(() => {
    refetch(groupIds);
  }, [refetch, groupIds]);
  useEffect(() => {
    if (!data) {
      return;
    }
    const next = createApprovalDrafts(data);
    setDrafts(next);
    setShared(getSharedApprovalValues(next));
    setOverwrite(false);
  }, [data]);

  return (
    <>
      <ModalBody p={0}>
        {loading && (
          <Box p={8}>
            <SpinnerWithLabel label={t('general.loadingData')} />
          </Box>
        )}
        {error && (
          <Box p={8}>
            <ErrorDetails error={error} />
            <Button onClick={() => refetch(groupIds)}>
              {t('general.retry')}
            </Button>
          </Box>
        )}
        {saveError && (
          <Box px={8} pt={4}>
            <ErrorDetails error={saveError} />
          </Box>
        )}
        {!loading && !error && initialized && drafts.length === 0 && (
          <Text p={8}>{t('productApproval.bulk.empty')}</Text>
        )}
        {!loading && !error && initialized && drafts.length > 0 && (
          <Box as="fieldset" disabled={saving} minW={0} border={0} p={0}>
            <Box
              px={{ base: 4, md: 8 }}
              py={6}
              bg="entityDirectory.raisedSurface"
              borderBottomWidth="1px"
              borderColor="entityDirectory.border"
            >
              <Flex justify="space-between" gap={4} wrap="wrap" mb={5}>
                <Box>
                  <Heading size="sm" mb={1}>
                    {t('productApproval.bulk.sharedSettings')}
                  </Heading>
                  <Text fontSize="sm" color="entityDirectory.mutedText">
                    {t('productApproval.bulk.sharedHelp')}
                  </Text>
                </Box>
                <Checkbox
                  isChecked={overwrite}
                  onChange={(event) => setOverwrite(event.target.checked)}
                  fontSize="sm"
                >
                  {t('productApproval.bulk.overwrite')}
                </Checkbox>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Flex direction="column" gap={4}>
                  {suppliers.map((sample) => (
                    <FormControl key={sample.supplierId}>
                      <FormLabel fontSize="sm">
                        {t('suppliers.supplierContact')}
                        {suppliers.length > 1
                          ? ` · ${sample.supplierName}`
                          : ''}
                      </FormLabel>
                      <ApprovalContactButton
                        contact={shared.suppliers[sample.supplierId]}
                        placeholder={t('productApproval.bulk.chooseSupplier')}
                        label={t('productApproval.bulk.supplierFor', {
                          name: sample.supplierName,
                        })}
                        onClick={() =>
                          selection.onOpen({
                            field: 'supplierContact',
                            referenceId: sample.supplierId,
                          })
                        }
                      />
                    </FormControl>
                  ))}
                </Flex>
                <Flex direction="column" gap={4}>
                  {customers.map((sample) => (
                    <FormControl key={sample.customerId}>
                      <FormLabel fontSize="sm">
                        {t('customer.customerContact')}
                        {customers.length > 1
                          ? ` · ${sample.customerName}`
                          : ''}
                      </FormLabel>
                      <ApprovalContactButton
                        contact={shared.customers[sample.customerId]}
                        placeholder={t('productApproval.bulk.chooseBrand')}
                        label={t('productApproval.bulk.brandFor', {
                          name: sample.customerName,
                        })}
                        onClick={() =>
                          selection.onOpen({
                            field: 'customerContact',
                            referenceId: sample.customerId,
                          })
                        }
                      />
                    </FormControl>
                  ))}
                </Flex>
                <FormControl>
                  <FormLabel fontSize="sm">
                    {t('productApproval.expectedReadyDate')}
                  </FormLabel>
                  <Input
                    type="date"
                    h={10}
                    borderRadius="4px"
                    variant="outline"
                    bg="entityDirectory.inputBackground"
                    borderColor="entityDirectory.border"
                    value={shared.sampleReadyDate ?? ''}
                    onChange={(event) => changeSharedDate(event.target.value)}
                  />
                  {!shared.sampleReadyDate && (
                    <Text
                      mt={1}
                      fontSize="xs"
                      color="entityDirectory.mutedText"
                    >
                      {t('productApproval.bulk.differentDates')}
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>
            </Box>
            <Box px={{ base: 4, md: 8 }} pt={6} pb={4}>
              <Flex justify="space-between" gap={3} wrap="wrap" mb={4}>
                <Heading size="sm">
                  {t('productApproval.bulk.approvalCount', {
                    count: drafts.length,
                  })}
                </Heading>
                <Text fontSize="sm" color="entityDirectory.mutedText">
                  {t('productApproval.bulk.overrideHelp')}
                </Text>
              </Flex>
              <Box
                overflowX="auto"
                borderWidth="1px"
                borderColor="entityDirectory.border"
                borderRadius="6px"
              >
                <Box minW="1080px">
                  <Flex
                    px={4}
                    py={3}
                    gap={4}
                    bg="entityDirectory.raisedSurface"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="entityDirectory.mutedText"
                    textTransform="uppercase"
                  >
                    <Box w="180px" flexShrink={0}>
                      {t('productApproval.bulk.articleVariant')}
                    </Box>
                    <Box w="130px" flexShrink={0}>
                      {t('general.branch')}
                    </Box>
                    <Box flex={1}>{t('suppliers.supplierContact')}</Box>
                    <Box flex={1}>{t('customer.customerContact')}</Box>
                    <Box w="160px" flexShrink={0}>
                      {t('productApproval.bulk.sampleReady')}
                    </Box>
                    <Box w="110px" flexShrink={0}>
                      {t('general.state')}
                    </Box>
                  </Flex>
                  {drafts.map((draft) => (
                    <BulkApprovalRow
                      key={draft.sample.compositionGroupId}
                      draft={draft}
                      onContact={(field) =>
                        selection.onOpen({
                          field,
                          referenceId:
                            field === 'supplierContact'
                              ? draft.sample.supplierId
                              : draft.sample.customerId,
                          groupId: draft.sample.compositionGroupId,
                        })
                      }
                      onDate={(date) =>
                        setField(
                          draft.sample.compositionGroupId,
                          'sampleReadyDate',
                          date,
                        )
                      }
                      onReset={(field) =>
                        resetField(draft.sample.compositionGroupId, field)
                      }
                      onToggle={() =>
                        setDrafts((current) =>
                          current.map((item) =>
                            item.sample.compositionGroupId ===
                            draft.sample.compositionGroupId
                              ? { ...item, ignored: !item.ignored }
                              : item,
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              </Box>
              {invalid.length > 0 && (
                <Alert
                  status="error"
                  mt={4}
                  borderRadius="md"
                  alignItems="flex-start"
                >
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t('productApproval.bulk.needsInput', {
                        count: invalid.length,
                      })}
                    </Text>
                    <Flex gap={3} wrap="wrap" mt={1}>
                      {invalid.map((draft) => (
                        <Link
                          key={draft.sample.compositionGroupId}
                          as="button"
                          type="button"
                          fontSize="sm"
                          textDecoration="underline"
                          onClick={() =>
                            goToApproval(draft.sample.compositionGroupId)
                          }
                        >
                          {[
                            draft.sample.articleItemCode,
                            draft.sample.matrixNodeXValue,
                            draft.sample.matrixNodeYValue,
                            draft.sample.branchName,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Link>
                      ))}
                    </Flex>
                  </Box>
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </ModalBody>
      <ModalFooter
        px={{ base: 4, md: 8 }}
        py={5}
        justifyContent="space-between"
        gap={4}
        flexWrap="wrap"
      >
        <Box>
          <Text fontSize="sm" fontWeight="semibold">
            {t('productApproval.bulk.total', { count: active.length })}
          </Text>
          <Text fontSize="sm" color="entityDirectory.mutedText">
            {t('productApproval.bulk.progress', {
              ready: readyCount,
              excluded: drafts.length - active.length,
            })}
          </Text>
        </Box>
        <Flex gap={3}>
          <Button
            variant="entityDirectorySecondary"
            onClick={onClose}
            isDisabled={saving}
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="entityDirectoryPrimary"
            onClick={submit}
            isLoading={saving}
            isDisabled={!canCreate}
          >
            {t('productApproval.bulk.create', { count: active.length })}
          </Button>
        </Flex>
      </ModalFooter>
      {selection.value && (
        <ContactsModal
          mode="select"
          filterOn={
            selection.value.field === 'supplierContact'
              ? ContactAddToType.Supplier
              : ContactAddToType.Customer
          }
          addTo={
            selection.value.field === 'supplierContact'
              ? ContactAddToType.Supplier
              : ContactAddToType.Customer
          }
          referenceId={selection.value.referenceId}
          isOpen={selection.isOpen}
          onClose={selection.onClose}
          onSelect={selectContact}
          onCreatedAndConnected={selectContact}
        />
      )}
    </>
  );
}
